import { ILoginTokenResponse } from '@aca-new/app/pages/login/shared/models/interfaces/token-response.interface';
import { AsideService } from '@aca-new/app/pages/main/shared/components/aside/shared/services/aside.service';
import { FreshChatService } from '@aca-new/app/pages/main/shared/services/fresh-chat.service';
import { IUser } from '@aca-new/app/pages/user/shared/models/interfaces/user.interface';
import { UserService } from '@aca-new/app/pages/user/shared/service/user.service';
import { IMyWindow } from '@aca-new/app/shared/interfaces/my-window.interface';
import { EChannelKeys } from '@aca-new/app/shared/models/enums/channel-keys.enum';
import { EPostMessageType } from '@aca-new/app/shared/models/enums/post-message-type.enum';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { AcaMessageService } from '@aca-new/app/shared/services/post-message-services/aca-message/aca-message.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { AppcuesService } from '@aca-new/app/shared/services/third-party-services/appcues/appcues.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, SecurityContext, ViewEncapsulation } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { QimaResponsiveService } from '@qima/ngx-qima';
import { filter, Observable, tap } from 'rxjs';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MainComponent implements OnInit, AfterViewInit {
  public isAsideCollapsed: boolean = false;
  public isAcaFrameVisible = true;
  public acaUrl: string = '';
  public isMobile: boolean = false;
  public isAppcuesBannerVisible = false;
  public readonly securityContext = SecurityContext;

  public constructor(
    private readonly _appcuesService: AppcuesService,
    private readonly _router: Router,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _asideService: AsideService,
    private readonly _translateService: TranslateService,
    private readonly _acaMessageService: AcaMessageService,
    private readonly _qimaResponsiveService: QimaResponsiveService,
    private readonly _appAuthenticationService: AppAuthenticationService,
    private readonly _freshChatService: FreshChatService,
    private readonly _storageService: StorageService,
    private readonly _userService: UserService,
    private readonly _appUserSettingService: AppUserSettingService,
    @Inject(WINDOW) private readonly _window: IMyWindow
  ) {}

  public ngOnInit(): void {
    this._watch();
    this._initData();
  }

  public ngAfterViewInit(): void {
    const userData: IUser = this._appAuthenticationService.getCurrentUser();

    this._initChat(userData);

    this._appAuthenticationService.currentUserSubject$.pipe(untilDestroyed(this)).subscribe((data: IUser): void => {
      this._initChat(data);
    });
  }

  private _initChat(userData: IUser): void {
    if (
      this._appAuthenticationService.isAuthenticated() &&
      !this._appUserSettingService.getIsLifeScience() &&
      !this._appUserSettingService.getIsQimaProduce() &&
      !this._window.parent.Cypress
    ) {
      this._freshChatService.initChat(userData);
    }
  }

  private _initData(): void {
    this._updateAcaFrameStatus(this._router.url);
    const url = this._acaMessageService.getAcaFrameUrl(this._router.url);

    this.acaUrl = this._appendEmbedHashData(url);
  }

  private _appendEmbedHashData(url: string): string {
    if (!url || !this._appAuthenticationService.isAuthenticated()) {
      return url;
    }

    const authData = this._appAuthenticationService.getAuthenticationData();
    const userData = this._appAuthenticationService.getCurrentUser();
    const loginData: Pick<ILoginTokenResponse, 'content'> = {
      content: {
        token: {
          id: authData.sessionId ?? '',
          loginType: authData.loginType ?? '',
          refreshKey: authData.refreshKey ?? '',
          token: authData.token ?? '',
          userId: authData.userId ?? '',
          userType: authData.userType ?? '',
          validBefore: authData.validBefore ?? '',
        },
        user: userData,
      },
    };
    const [baseUrl, existingHash = ''] = url.split('#');
    const pairs = existingHash.split('&').filter((p: string): boolean => !!p && !p.startsWith('ld='));

    pairs.push(`ld=${encodeURIComponent(JSON.stringify(loginData))}`);

    return `${baseUrl}#${pairs.join('&')}`;
  }

  private _watch(): void {
    this._asideService.isAsideCollapsed$.pipe(untilDestroyed(this)).subscribe((isAsideCollapsed: boolean): void => {
      this.isAsideCollapsed = isAsideCollapsed;
      this._changeDetectorRef.markForCheck();
    });

    this._watchAppcues$().pipe(untilDestroyed(this)).subscribe();
    this._watchRouterForAppcues$().pipe(untilDestroyed(this)).subscribe();

    this._qimaResponsiveService
      .isMobile$()
      .pipe(untilDestroyed(this))
      .subscribe((isMobile: boolean): void => {
        this.isMobile = isMobile;
      });

    if (this._acaMessageService.isAcaFrameAvailable) {
      this._watchRouterChange$().pipe(untilDestroyed(this)).subscribe();
      this._acaMessageService.watchMessageFromAca();
    }

    this._updateLoginInfo();
    this._updateUserData();
  }

  private _updateLoginInfo(): void {
    const channel = new BroadcastChannel(EChannelKeys.LOGIN_AGAIN);

    channel.onmessage = (event): void => {
      const { params, user } = event.data;
      const userId = this._storageService.getItem(EStorageKeys.USER_ID);

      if (!params || !user || userId === params.userId) {
        return;
      }

      this._userService.saveAuthenticationData(params as Params);
      this._userService.updateUserDataSession(user as IUser);
      void this._router.navigate(['/home']).then((isNavigated: boolean): void => {
        if (isNavigated) {
          this._window.location.reload();
        }
      });
    };
  }

  private _updateUserData(): void {
    const channel = new BroadcastChannel(EChannelKeys.UPDATE_USER_DATA);

    channel.onmessage = (event): void => {
      const { user } = event.data;

      if (user) {
        this._userService.updateUserDataSession(user as IUser);
        this._window.location.reload();
      }
    };
  }

  private _watchRouterChange$(): Observable<Event> {
    let previousUrl: string = '';

    return this._router.events.pipe(
      filter((event: Event): boolean => {
        return event instanceof NavigationStart || event instanceof NavigationEnd;
      }),
      tap({
        next: (event: Event): void => {
          if (event instanceof NavigationStart) {
            const isGotoBookings: boolean = event.url.startsWith('/bookings');

            if (event.navigationTrigger === 'popstate' && (!this._acaMessageService.isMyQimaPageAvailable(event.url) || isGotoBookings)) {
              this._acaMessageService.pathChangeHandler(event.url.replace('/aca', ''));

              // If navigating from ACA booking draft edit page to bookings/drafts, stay on draft page and wait for user to confirm leaving draft page through dialog
              if (event.url === '/bookings/drafts' && previousUrl.startsWith('/aca')) {
                void this._router.navigateByUrl(this._router.routerState.snapshot.url, { skipLocationChange: false });
              }
            }
          } else if (event instanceof NavigationEnd) {
            this._updateAcaFrameStatus(event.urlAfterRedirects);
            previousUrl = event.url;
          }
        },
      })
    );
  }

  private _watchAppcues$(): Observable<boolean> {
    return this._appcuesService.appcuesEvent$().pipe(
      tap({
        next: (isVisible): void => {
          this.isAppcuesBannerVisible = isVisible;
          this._changeDetectorRef.markForCheck();
        },
      })
    );
  }

  private _watchRouterForAppcues$(): Observable<Event> {
    return this._router.events.pipe(
      filter((event: Event): boolean => {
        return event instanceof NavigationEnd;
      }),
      tap({
        next: (): void => {
          this._appcuesService.toggleBanner(false);
        },
      })
    );
  }

  private _updateAcaFrameStatus(url: string): void {
    const wasAcaFrameVisible = this.isAcaFrameVisible;

    this.isAcaFrameVisible = !this._acaMessageService.isMyQimaPageAvailable(url) && url.startsWith('/aca');

    if (this.isAcaFrameVisible && !wasAcaFrameVisible) {
      this._acaMessageService.postMessageToAca({
        type: EPostMessageType.LANGUAGE_CHANGE,
        data: { language: this._translateService.currentLang },
      });
    }

    this._changeDetectorRef.markForCheck();
  }
}
